import { PaymentSetupForm, UnsavedChangesModal } from '@app/components'
import { SugarPaymentSetupType } from '@app/types'
import { Container } from '@mantine/core'
import { useIsMutating } from '@tanstack/react-query'
import { useRouter } from 'next/router'
import { type FC, useEffect, useState } from 'react'

export interface PaymentSetupFormModalProps {
  onClose: () => void
  onSuccess: () => void
  opened: boolean
  paymentSetupType: SugarPaymentSetupType
}

// Two remount defences here, both required — either alone would probably work, but this repo has no
// test infrastructure to catch a regression, so belt and braces:
//
// 1. `key={paymentSetupType}` forces React to discard the previous PaymentSetupForm instance and
//    mount a fresh one whenever the type changes, so its useForm re-reads initialValues.
// 2. The page that renders this component does so conditionally (`{setupTarget && <PaymentSetupFormModal
//    .../>}`), so this whole component — including the isChanged state below — unmounts on close
//    instead of merely toggling `opened`. Modal's keepMounted={false} unmounts Modal's *children*, but
//    it does not unmount the component that renders <Modal>, which is exactly the mistake in
//    AuthenticatorSetupModal's handleClose (it resets some state and forgets the rest).
//
// Without both, a form opened for ACH, closed, then reopened for W-9 could submit the ACH payload
// shape while the user believes they are updating their W-9 — a silent wrong-record write of banking
// data.
export const PaymentSetupFormModal: FC<PaymentSetupFormModalProps> = ({
  onClose,
  onSuccess,
  opened,
  paymentSetupType
}) => {
  const router = useRouter()
  const [isChanged, setIsChanged] = useState(false)
  // usePaymentSetup declares `mutationKey: ['paymentSetup']`, so the in-flight signal is readable from
  // out here without threading any new prop through PaymentSetupForm.
  const isSubmitting = useIsMutating({ mutationKey: ['paymentSetup'] }) > 0

  // An in-flight banking write cannot be recalled, so it must not be interruptible. Closing this modal
  // only unmounts the form — the mutation is owned by the QueryClient, so the POST still completes, the
  // record is still written and the success notification still fires, while the user believes they
  // cancelled. While the write is in flight there must therefore be no way out of the modal at all:
  //
  //  * `withCloseButton={!isSubmitting}` below removes the X. `closeOnEscape` and `closeOnClickOutside`
  //    are already false for every Modal (MantineProvider), so that leaves no dismissal path in the UI.
  //  * the beforeunload listener covers refresh / tab close.
  //  * the two router guards cover in-app navigation and browser Back respectively.
  useEffect(() => {
    if (!isSubmitting) {
      return
    }

    // Captured while the guard is armed so the popstate handler can put the entry back exactly as it
    // was, rather than synthesising one.
    const restoreState = window.history.state
    const restoreUrl = window.location.href
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault()
      // some browsers still require returnValue to be set before they will show the native prompt
      event.returnValue = ''
    }
    // Link clicks and router.push/replace: Next emits routeChangeStart BEFORE Router.changeState writes
    // to history (emit and changeState are separate steps of the same `change()` call in
    // next/dist/shared/lib/router/router.js), so throwing here aborts the navigation without mutating
    // the history stack at all. This is Next's own documented abort pattern; the thrown value is
    // swallowed, and routeChangeError is emitted first so RouterTransition's progress bar completes
    // instead of hanging.
    const handleRouteChangeStart = () => {
      router.events.emit('routeChangeError')

      throw 'Route change aborted: a payment setup submission is in flight.'
    }

    // Browser Back/Forward is the one case the throw above cannot clean up after: the browser has
    // already moved the history pointer by the time popstate fires, so aborting the route change would
    // leave the address bar showing the previous page while this modal stayed on screen. Returning
    // false stops Next handling the event at all, and re-pushing the captured entry restores the URL.
    // This does not grow the stack — pushState truncates forward entries, so [prev, payment-info] ends
    // up as [prev, payment-info] again, not [prev, payment-info, payment-info].
    router.beforePopState(() => {
      window.history.pushState(restoreState, '', restoreUrl)

      return false
    })
    router.events.on('routeChangeStart', handleRouteChangeStart)
    window.addEventListener('beforeunload', handleBeforeUnload)

    return () => {
      // `() => true` is beforePopState's documented no-op: it restores Next's default popstate handling.
      router.beforePopState(() => true)
      router.events.off('routeChangeStart', handleRouteChangeStart)
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [isSubmitting, router])

  return (
    <UnsavedChangesModal
      fullScreen
      isChanged={isChanged}
      isLoading={isSubmitting}
      onClose={onClose}
      opened={opened}
      title={paymentSetupType === SugarPaymentSetupType.W9 ? 'Form W-9' : 'ACH Authorization Form'}
      withCloseButton={!isSubmitting}
    >
      {/* Same Container size as the main app layout (AppLayout), so the fields do not stretch the whole
      width of a wide monitor. `key` stays on PaymentSetupForm and is deliberately NOT moved out here —
      keying the Container instead would lose remount defence 1 described above. */}
      <Container
        px={0}
        size='xl'
      >
        <PaymentSetupForm
          key={paymentSetupType}
          onDirtyChange={setIsChanged}
          onSuccess={onSuccess}
          paymentSetupType={paymentSetupType}
        />
      </Container>
    </UnsavedChangesModal>
  )
}
